import React, { useState, useEffect, useRef } from 'react';
import { X, Search, MessageSquare, Send, User, Clock } from 'lucide-react';
import modalsBg from '../assets/Modals.jpg';
import api from '../services/api';
import toast from 'react-hot-toast';
import SkeletonChat from './SkeletonChat';

const ChatsModal = ({ isOpen, onClose, user, initialPropertyId, initialReceiverId }) => {
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (isOpen && user) {
      fetchChats();
      // Poll for new messages every 3 seconds
      pollIntervalRef.current = setInterval(() => {
        if (selectedChat) {
          fetchMessages(selectedChat.id, true);
        }
      }, 3000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, user, selectedChat]);

  useEffect(() => {
    console.log('ChatsModal useEffect - isOpen:', isOpen);
    console.log('ChatsModal useEffect - initialPropertyId:', initialPropertyId);
    console.log('ChatsModal useEffect - initialReceiverId:', initialReceiverId);
    
    if (isOpen && initialPropertyId && initialReceiverId) {
      console.log('Creating/getting chat for property:', initialPropertyId, 'receiver:', initialReceiverId);
      createOrGetChat(initialPropertyId, initialReceiverId);
    }
  }, [isOpen, initialPropertyId, initialReceiverId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChats = async () => {
    try {
      setLoading(true);
      const data = await api.getChats();
      setChats(data);
    } catch (error) {
      console.error('Failed to fetch chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (chatId, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await api.getChatMessages(chatId);
      setMessages(data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const createOrGetChat = async (propertyId, receiverId) => {
    try {
      console.log('createOrGetChat called with:', { propertyId, receiverId });
      const chat = await api.createChat(propertyId, receiverId);
      console.log('Chat created/retrieved:', chat);
      
      await fetchChats();
      console.log('Setting selected chat:', chat);
      setSelectedChat(chat);
      
      await fetchMessages(chat.id);
      console.log('Messages fetched for chat:', chat.id);
    } catch (error) {
      console.error('Failed to create chat:', error);
      toast.error('Failed to open chat: ' + error.message);
    }
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (message.trim() && selectedChat) {
      try {
        await api.sendMessage(selectedChat.id, message);
        setMessage('');
        await fetchMessages(selectedChat.id);
        await fetchChats(); // Update chat list with new last message
      } catch (error) {
        console.error('Failed to send message:', error);
        toast.error('Failed to send message. Please try again.');
      }
    }
  };

  const handleChatSelect = async (chat) => {
    setSelectedChat(chat);
    await fetchMessages(chat.id);
  };

  const filteredChats = chats.filter(chat =>
    chat.other_user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.property_title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-5xl h-[92vh] sm:h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between" style={{ backgroundImage: `url(${modalsBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="flex items-center gap-3">
            <MessageSquare className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">Messages</h2>
              <p className="text-blue-100 text-xs sm:text-sm">Chat with property seekers and owners</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Chat List */}
          <div className={`${selectedChat ? 'hidden md:flex' : 'flex'} w-full md:w-80 border-r border-gray-200 flex-col bg-gray-50`}>
            {/* Search */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                />
              </div>
            </div>

            {/* Chat List Items */}
            <div className="flex-1 overflow-y-auto">
              {loading && chats.length === 0 ? (
                <div>
                  {[...Array(4)].map((_, index) => (
                    <SkeletonChat key={index} />
                  ))}
                </div>
              ) : filteredChats.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  {searchQuery ? 'No chats found' : 'No conversations yet'}
                </div>
              ) : (
                filteredChats.map((chat) => (
                  <button
                    key={chat.id}
                    onClick={() => handleChatSelect(chat)}
                    className={`w-full p-4 flex items-start gap-3 hover:bg-white transition-colors border-b border-gray-200 ${
                      selectedChat?.id === chat.id ? 'bg-white border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="relative">
                      <img
                        src={chat.other_user_photo || 'https://via.placeholder.com/48?text=User'}
                        alt={chat.other_user_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      {chat.unread_count > 0 && (
                        <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                          {chat.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-gray-900 text-sm">{chat.other_user_name}</h3>
                        <span className="text-xs text-gray-500">
                          {chat.last_message_time ? getTimeAgo(chat.last_message_time) : ''}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-1 truncate">{chat.property_title}</p>
                      <p className={`text-sm truncate ${chat.unread_count > 0 ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                        {chat.last_message || 'No messages yet'}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`${selectedChat ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-white`}>
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedChat(null)}
                      className="md:hidden w-9 h-9 rounded-full bg-white border border-gray-200 text-gray-600 flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <img
                      src={selectedChat.other_user_photo || 'https://via.placeholder.com/48?text=User'}
                      alt={selectedChat.other_user_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{selectedChat.other_user_name}</h3>
                      <p className="text-xs text-gray-500 truncate max-w-[180px] sm:max-w-none">Regarding: {selectedChat.property_title}</p>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 bg-gradient-to-b from-gray-50 to-white">
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] sm:max-w-xs px-4 py-3 rounded-2xl ${
                            msg.sender_id === user?.id
                              ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white'
                              : 'bg-gray-200 text-gray-900'
                          }`}
                        >
                          <p className="text-sm">{msg.message}</p>
                          <p className={`text-xs mt-1 ${msg.sender_id === user?.id ? 'text-blue-100' : 'text-gray-500'}`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-3 sm:p-4 border-t border-gray-200 bg-white">
                  <div className="flex gap-2 sm:gap-3">
                    <input
                      type="text"
                      placeholder="Type your message..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="flex-1 px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                    />
                    <button
                      type="submit"
                      disabled={!message.trim()}
                      className="px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <Send className="w-4 h-4" />
                      <span className="hidden sm:inline">Send</span>
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">Select a conversation</p>
                  <p className="text-sm">Choose a chat from the list to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatsModal;
